import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import PlatformConditional from 'Components/PlatformConditional';
import { isPartialDate, sortPartialDate } from 'neuro-utils';
import { equals } from 'ramda';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';
import { platforms } from '../../../config/platformSettings';
import { diagnosticExamCodes } from '../utils';

import type { TClpAndCnpTestsDocumentType, TRelevantDocumentType } from '../utils';

const getLatestDocs = (
  docs: IDocumentWithTypeV2<TRelevantDocumentType>[],
): IDocumentWithTypeV2<TClpAndCnpTestsDocumentType>[] | undefined => {
  const respiratoryFailureDiagnosis = filterByDocumentType(docs, 'diagnosis').filter(
    d => d.diagnosis === 'J96.1' || d.diagnosis === 'J96.9' || d.diagnosis === 'respiratoryFailureSuspicion',
  );

  const carbonDioxideMeasurementDocs =
    respiratoryFailureDiagnosis.length > 0 ? filterByDocumentType(docs, 'carbonDioxideMeasurement') : [];

  const sortedArrOfLatest = [
    ...filterDocumentsByType(docs, ['sleepPolygraphy', 'sleepStudy', 'spirometry', 'nightOximetry']),
    ...carbonDioxideMeasurementDocs,
  ].sort((n1, n2) => sortPartialDate(n2.date, n1.date) || n2._cdate - n1._cdate);
  return sortedArrOfLatest;
};

const getTitle = (type: string): React.JSX.Element => <FormattedMessage id={`clpAndCnpTests.${type}`} />;

const ClpAndCnpTestsDash = ({ documents }: IOwnProps): React.JSX.Element => {
  const sortedDocs = getLatestDocs(documents);
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const diagnosticExamDocs = filterByDocumentType(documents, 'diagnosticExamination');
  const cnpTileDiagnosticExams = diagnosticExamDocs.filter(d =>
    Object.values(diagnosticExamCodes).some(ec => equals(ec, d.code)),
  );

  return sortedDocs ? (
    <>
      <PlatformConditional platform={platforms.filter(p => p !== 'mgravis')}>
        <TileContentMaker
          type="recentList"
          data={sortedDocs.map(d => ({
            date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
            title: getTitle(d._type),
            key: d._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform="mgravis">
        <TileContentMaker
          type="recentList"
          data={cnpTileDiagnosticExams.map(d => ({
            date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
            title: fm(`clpAndCnpTests.mgravis.${d.code?.code}`),
            value: fm(`clpAndCnpTests.mgravis.opts.${d.finding?.type}`),
            key: d._id,
          }))}
        />
      </PlatformConditional>
    </>
  ) : (
    <></>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[];
}

export default ClpAndCnpTestsDash;
